import { useState, useEffect } from 'react';
import { makeCall } from '@services/twilioService';
import { initiateIvrCall } from '@services/api';
import { useCxInboundTest } from '@hooks/useCallExperience';
import { parseApiError, parseIvrBlockedMessage } from '@utils/errorUtils';
import TestCallInfo from './TestCallInfo';
import PatientPicker, { PRODUCT_LINE_NAMES } from './PatientPicker';

const ROUTINGS = {
  phonesystem: { cls: 'cx-badge-co', label: 'Phoenix_PhoneSystem', note: 'Calls hear this page’s wording.' },
  backend: { cls: 'cx-badge-warn', label: 'Phoenix_Back_End (legacy)', note: 'Not switched over yet: callers hear the legacy system, not this page.' },
  'twiml-app': { cls: 'cx-badge-bi', label: 'TwiML app', note: 'Routed through a TwiML app.' },
  other: { cls: 'cx-badge-bi', label: 'Other URL', note: 'Twilio sends it somewhere else.' },
  'not-in-twilio': { cls: 'cx-badge-off', label: 'Not on this Twilio account', note: 'Dialing it won’t reach this environment.' },
  unknown: { cls: 'cx-badge-bi', label: 'Couldn’t check Twilio', note: 'The Twilio lookup failed; try again.' },
};

const formatTime = (t) => (t ? t.slice(0, 5) : '');

function InboundTest({ ctx, settings }) {
  const { data: test, isLoading, error, refetch, isFetching } = useCxInboundTest(ctx.companyId, true);
  if (isLoading) return <div className="cx-muted">Checking numbers…</div>;
  if (error) return <div className="cx-error">{parseApiError(error)}</div>;

  const stateText = !test.companyActive
    ? 'the company is inactive, so callers hear the inactive-company message and are hung up'
    : test.state === 'holiday' ? `it’s ${test.holidayName}, so callers hear the holiday message and can leave a voicemail`
      : test.state === 'closed' ? `it’s outside routing hours (${formatTime(settings?.routingHoursStart) || 'none set'}–${formatTime(settings?.routingHoursEnd)}), so callers hear the after-hours message`
        : `callers go to the ${settings?.teamName || 'team'} queue`;

  return (
    <>
      <div className={`cx-callout${test.state === 'open' && test.companyActive ? '' : ' cx-callout-warn'}`}>
        Right now ({formatTime(test.now)} Central), {stateText}.
        <button type="button" className="cx-link" onClick={() => refetch()} disabled={isFetching}> Check again</button>
      </div>

      <h4>1. Dial one of these numbers</h4>
      {test.numbers.length === 0 && <div className="cx-muted">No inbound numbers are set up for {ctx.name}.</div>}
      <table className="cx-tc-table">
        <tbody>
          {test.numbers.map((n) => {
            const r = ROUTINGS[n.routing] || ROUTINGS.unknown;
            return (
              <tr key={n.e164}>
                <td className="cx-tc-num">{n.display}</td>
                <td className="cx-small">{n.sources.join(', ')}{n.isIvr && <div className="cx-muted">IVR number: hands the call to the legacy IVR.</div>}</td>
                <td><span className={`cx-badge ${r.cls}`} title={n.voiceUrl || ''}>{r.label}</span><div className="cx-muted cx-small">{r.note}</div></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="cx-hint">Callers are recognized by the number they call from. Calling from a number on one of {ctx.name}&apos;s patients plays that patient&apos;s messages (and Spanish if they&apos;re set to Spanish); any other number hears the defaults.</p>

      {test.state === 'open' && test.companyActive && (
        <>
          <h4>2. Answer it as a coach</h4>
          <ol className="cx-tc-steps">
            <li>Open the <b>Phone Controls</b> tab and make sure the phone shows <b>Ready</b> at the top.</li>
            <li>Watch the <b>{settings?.teamName || 'team'}</b> queue and answer the call when it shows up.</li>
            <li>Come back to Call Experience to follow the call. It keeps going, and this panel stays open.</li>
          </ol>
        </>
      )}
    </>
  );
}

function OutboundTest({ ctx, phone, onCallPlaced }) {
  const [patient, setPatient] = useState(null);
  const [number, setNumber] = useState('');
  const [productLine, setProductLine] = useState('');
  const [error, setError] = useState('');
  const [calling, setCalling] = useState(false);

  useEffect(() => {
    setNumber(patient?.phoneNumber || '');
    setProductLine(patient?.productLines.length === 1 ? PRODUCT_LINE_NAMES[patient.productLines[0]] : '');
  }, [patient]);

  const busy = phone.callStatus && phone.callStatus !== 'idle';
  const canCall = phone.isReady && !busy && !calling && number.trim();

  const call = async () => {
    setError('');
    setCalling(true);
    try {
      const c = await makeCall(number.trim(), { patientGuid: patient?.guid, productLine: productLine || undefined });
      phone.onCallConnectionReady?.(c);
      // The browser leg's SID is the contact record's parent call SID.
      c.on('accept', (call) => onCallPlaced(call.parameters?.CallSid));
      c.on('disconnect', () => phone.onCallEnded?.());
      c.on('error', (err) => { setError(`Call error: ${err.message}`); phone.onCallEnded?.(); });
      phone.onCallStarted?.(number.trim());
    } catch (err) {
      setError(`Couldn’t place the call: ${err.message}`);
    } finally {
      setCalling(false);
    }
  };

  return (
    <>
      <h4>1. Pick a patient of {ctx.name}</h4>
      <PatientPicker companyId={ctx.companyId} selected={patient} onSelect={setPatient} />

      <h4>2. Call from this browser</h4>
      <div className="cx-tc-row">
        <label className="cx-tc-field">Ring
          <input className="cx-tc-input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="+15551234567" />
        </label>
        <label className="cx-tc-field">Product line
          <select value={productLine} onChange={(e) => setProductLine(e.target.value)}>
            <option value="">Not set</option>
            <option value="CPAP">CPAP</option>
            <option value="CGM">CGM</option>
          </select>
        </label>
        <button type="button" className="cx-btn cx-btn-primary" disabled={!canCall} onClick={call}>📞 Call</button>
      </div>
      <p className="cx-hint">
        Put your own phone in &quot;Ring&quot; to take the patient&apos;s side yourself; the call is still logged against the patient.
        {!phone.isReady && <b> The browser phone isn&apos;t ready yet. Open Phone Controls once to sign it in.</b>}
        {busy && <b> You&apos;re already on a call; use the call bar to hang up first.</b>}
      </p>
      {error && <div className="cx-error">{error}</div>}
      {busy && <div className="cx-callout">On the call: use the call bar at the bottom of the page to mute, send digits, transfer or hang up.</div>}
    </>
  );
}

function IvrTest({ journey, ctx, settings, onCallPlaced }) {
  const [patient, setPatient] = useState(null);
  const [number, setNumber] = useState('');
  const [override, setOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const productLineId = journey.productLineId;
  const lineName = PRODUCT_LINE_NAMES[productLineId];

  useEffect(() => { setNumber(patient?.phoneNumber || ''); setResult(null); setError(''); }, [patient]);

  const line = settings?.productLines.find((p) => p.id === productLineId);
  const warnings = [
    settings && !settings.ivr.enabled && `${ctx.name} has IVR turned off, so the call will be blocked.`,
    settings?.ivr.paused && `${ctx.name} has IVR paused, so the call will be blocked.`,
    settings && !line?.active && `${ctx.name} has no active ${lineName} product line.`,
    patient && !patient.productLines.includes(productLineId) && `This patient has no ${lineName} product line, so the IVR may find nothing to offer.`,
  ].filter(Boolean);

  const start = async () => {
    setError('');
    setResult(null);
    setSubmitting(true);
    try {
      const started = await initiateIvrCall({ patientGuid: patient.guid, phoneNumber: number.trim() || undefined, overrideDailyLimit: override, productLineId });
      setResult(started);
      onCallPlaced(started.callSid);
    } catch (err) {
      setError(parseIvrBlockedMessage(err) || `Couldn’t start the call: ${parseApiError(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="cx-callout cx-callout-warn">
        This places a real IVR call. Finishing the order creates a real {lineName} order for the patient and moves their outreach dates, so use a test patient.
      </div>
      {warnings.map((w) => <div key={w} className="cx-callout cx-callout-warn">{w}</div>)}

      <h4>1. Pick a patient of {ctx.name}</h4>
      <PatientPicker companyId={ctx.companyId} selected={patient} onSelect={setPatient} productLineId={productLineId} />

      {patient && (
        <>
          <div className="cx-tc-answers">
            <div><span className="cx-muted cx-small">Birth year to enter</span><b>{patient.birthYear ?? 'none on file'}</b></div>
            <div><span className="cx-muted cx-small">Greeting language</span><b>{patient.spanish ? 'Spanish' : 'English'}</b></div>
          </div>
          {patient.birthYear == null && <p className="cx-hint">With no birth year on file the IVR can&apos;t verify the patient and ends the call.</p>}

          <h4>2. Start the call</h4>
          <div className="cx-tc-row">
            <label className="cx-tc-field">Ring
              <input className="cx-tc-input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="+15551234567" />
            </label>
            <button type="button" className="cx-btn cx-btn-primary" disabled={submitting || !!result} onClick={start}>
              {submitting ? 'Starting…' : `📞 Start ${lineName} IVR call`}
            </button>
            {result && <button type="button" className="cx-btn" onClick={() => setResult(null)}>Call again</button>}
          </div>
          <label className="cx-scen-check">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
            Skip the once-a-day limit (dev and QA only; ignored on stage and prod)
          </label>
          <p className="cx-hint">Put your own phone in &quot;Ring&quot; to take the call yourself; the IVR still speaks as this patient.</p>
          {error && <div className="cx-error">{error}</div>}
          {result && <div className="cx-success">Calling {result.to} from {result.from}. Answer it and follow along in the journey. <span className="cx-muted cx-small">{result.callSid}</span></div>}

        </>
      )}
    </>
  );
}

/**
 * Test call: a pane docked beside the journey (not a modal), so the call flow stays in view during the
 * call. Places or receives a real call for the viewed company: live numbers for inbound, a browser-phone
 * dial for the outbound coach call, and a real IVR call for the resupply IVR.
 */
export default function TestCallPanel({ journey, ctx, settings, phone, onClose }) {
  const [placedCallSid, setPlacedCallSid] = useState(null);
  return (
    <aside className="cx-testpane" aria-label="Test call">
      <header>
        <h3>▶ Test call</h3>
        <span className="cx-spacer" />
        <button type="button" className="cx-btn cx-btn-small" onClick={onClose} aria-label="Close test call">✕</button>
      </header>
      <div className="cx-muted cx-small">{journey.title} · {ctx.name}</div>
      {!ctx.isCompany && (
        <div className="cx-simempty">
          Test calls run as a real company. Pick a company in <b>Viewing</b> to test {ctx.isParent ? 'a member of this group' : 'its calls'}.
        </div>
      )}
      {ctx.isCompany && journey.id === 'inbound' && <InboundTest ctx={ctx} settings={settings} />}
      {ctx.isCompany && journey.id === 'outbound-coach' && <OutboundTest ctx={ctx} phone={phone} onCallPlaced={setPlacedCallSid} />}
      {ctx.isCompany && journey.id === 'resupply-ivr' && <IvrTest journey={journey} ctx={ctx} settings={settings} onCallPlaced={setPlacedCallSid} />}
      {ctx.isCompany && <TestCallInfo key={ctx.companyId} companyId={ctx.companyId} callSid={placedCallSid} />}
    </aside>
  );
}
